import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-compass-drafting',
    title: '1. Full-Lifecycle Software Development',
    desc: 'From initial design to production deployment, we build scalable, resilient web, mobile, and desktop applications tailored to modern market demands.',
  },
  {
    icon: 'fa-solid fa-pen-ruler',
    title: '2. UI/UX Design & Prototyping',
    desc: 'Craft intuitive, user-centric interfaces and interactive prototypes that optimize customer engagement, conversion rates, and overall product usability.',
  },
  {
    icon: 'fa-solid fa-cubes',
    title: '3. Cloud-Native Microservices Architecture',
    desc: 'Engineer modular, decoupled systems using microservices, serverless functions, and containerized deployments for max scalability and fault tolerance.',
  },
  {
    icon: 'fa-solid fa-rocket',
    title: '4. MVP Development & Rapid Prototyping',
    desc: 'Turn product visions into market-ready MVPs fast, allowing startups and enterprise teams to validate features and collect real user feedback.',
  },
  {
    icon: 'fa-solid fa-code-merge',
    title: '5. DevOps & Automated CI/CD Pipelines',
    desc: 'Streamline code integration and release cycles with automated testing, infrastructure as code (IaC), and seamless zero-downtime deployment pipelines.',
  },
  {
    icon: 'fa-solid fa-vial-circle-check',
    title: '6. Quality Assurance & Automated Testing',
    desc: 'Ensure rock-solid software quality through comprehensive end-to-end (E2E), regression, performance, and automated unit testing frameworks.',
  },
  {
    icon: 'fa-solid fa-wrench',
    title: '7. Legacy Product Modernization',
    desc: 'Transform monolithic systems into high-performance modern web platforms using updated tech stacks, optimized APIs, and refreshed UI designs.',
  },
  {
    icon: 'fa-solid fa-diagram-next',
    title: '8. SaaS Multi-Tenant Engineering',
    desc: 'Architect secure, multi-tenant SaaS platforms with role-based access control, automated billing integration, and isolated data partitions.',
  },
  {
    icon: 'fa-solid fa-mobile-screen-button',
    title: '9. Cross-Platform Mobile Engineering',
    desc: 'Deliver native-grade mobile experiences on iOS and Android from a single code base using modern frameworks like React Native and Flutter.',
  },
  {
    icon: 'fa-solid fa-shield-virus',
    title: '10. Product Security & DevSecOps',
    desc: 'Embed security into every phase of development with static/dynamic code analysis, vulnerability scanning, and OWASP compliance guidelines.',
  },
];

const whyChooseUs = [
  'Full-lifecycle development from ideation to scale',
  'Agile, sprint-driven development methodologies',
  'User-first UX/UI design and rapid prototyping',
  'Robust cloud-native microservices architecture',
  'Automated CI/CD pipelines for continuous deployment',
  'Strict compliance with OWASP security standards',
  'Cross-platform mobile and web engineering',
  'Transparent sprint tracking and clear communication',
  'Scalable codebases built for long-term maintainability',
  'Dedicated QA testing and automated release checks',
];

const processSteps = [
  { step: '01', title: 'Ideation & Product Discovery', desc: 'Define user personas, core feature sets, technical feasibility, and product roadmap.' },
  { step: '02', title: 'UI/UX Design & Wireframing', desc: 'Design wireframes, high-fidelity prototypes, and design systems for seamless usability.' },
  { step: '03', title: 'Architecture Blueprinting', desc: 'Select tech stacks, map microservices, design databases, and set up cloud environments.' },
  { step: '04', title: 'Agile Development & Sprints', desc: 'Build scalable product features incrementally with continuous code reviews and demos.' },
  { step: '05', title: 'Automated Testing & QA', desc: 'Conduct load, vulnerability, and E2E automation testing to ensure release readiness.' },
  { step: '06', title: 'Launch & Continuous Support', desc: 'Deploy to cloud production environments, track user analytics, and continuously improve features.' },
];

const techStack = [
  { category: 'Frontend Development', items: ['React.js', 'Next.js', 'Vue.js', 'TypeScript', 'Tailwind CSS'] },
  { category: 'Backend & APIs', items: ['Node.js', 'Python / Django', 'Java / Spring Boot', 'Go', 'GraphQL', 'REST'] },
  { category: 'Mobile Frameworks', items: ['React Native', 'Flutter', 'Swift (iOS)', 'Kotlin (Android)'] },
  { category: 'Cloud, DevOps & Testing', items: ['AWS', 'Docker', 'Kubernetes', 'Jest', 'Cypress', 'GitHub Actions'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ProductEngineering = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Product Engineering
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Product <span className="gradient-text-cyan">Engineering</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we turn concepts into market-defining digital products. From UX design to cloud-native microservices, we build scalable software engineered for growth, reliability, and exceptional user experiences.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Build Your Product With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Product Engineering Services by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Software Product Tech Stack</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Engineering Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Bring Your Software Product Vision to Life with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Partner with experienced software architects and engineers to build market-ready applications engineered for enterprise scale.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Product Engineer &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductEngineering;